/**
 * `useTelegramLink` — the member's own Telegram link (#2072).
 *
 *   * `GET    /api/account/me/telegram`            → status + @handle
 *   * `POST   /api/account/me/telegram/link-token` → the `t.me/<bot>?start=…` link
 *   * `DELETE /api/account/me/telegram`            → disconnect
 *   * `GET    /api/account/me/telegram/claim-preview?t=` → whose chat a link
 *     names (first name, @handle) — read, never burned (#2071)
 *   * `POST   /api/account/me/telegram/claim`      → claim the chat a
 *     `/connect/telegram?t=<token>` link names (#2071); 410 = expired
 *
 * A 404 (`telegram_off`) means no team of the member's has Telegram on — the
 * Profile sheet hides its row and the screen says so. Never retried: an off
 * component is an answer, not a failure.
 *
 * `useTeamTelegramStatus(teamId)` is the lead's People column read: status
 * per membership, never the handle.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { ApiError, apiFetch } from "./client";
import type { components } from "./schema.gen";

export type TelegramLinkStatus = components["schemas"]["TelegramLinkStatus"];
export type TelegramLinkToken = components["schemas"]["TelegramLinkToken"];
export type TelegramClaimResult = components["schemas"]["TelegramClaimResult"];
export type TelegramClaimPreview = components["schemas"]["TelegramClaimPreview"];
export type MemberTelegramStatus = components["schemas"]["MemberTelegramStatus"];
export type TeamTelegramStatusResponse = components["schemas"]["TeamTelegramStatusResponse"];

export const TELEGRAM_LINK_KEY = (accountId: string | null | undefined) =>
  ["me", "telegram", accountId ?? ""] as const;

function isOff(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * The member's link. `available === false` once the server says no team has
 * Telegram on (404); `undefined` while loading.
 */
export function useTelegramLink(enabled = true) {
  const { accountId } = useAuth();
  const query = useQuery<TelegramLinkStatus, Error>({
    queryKey: TELEGRAM_LINK_KEY(accountId),
    enabled: enabled && Boolean(accountId),
    retry: false,
    staleTime: 30_000,
    queryFn: () => apiFetch<TelegramLinkStatus>("/api/account/me/telegram"),
  });
  const available = query.isPending ? undefined : !isOff(query.error);
  return { ...query, available };
}

export function useTelegramLinkToken() {
  return useMutation<TelegramLinkToken, Error, void>({
    mutationFn: () =>
      apiFetch<TelegramLinkToken>("/api/account/me/telegram/link-token", { method: "POST" }),
  });
}

export function useDisconnectTelegram() {
  const { accountId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<TelegramLinkStatus, Error, void>({
    mutationFn: () =>
      apiFetch<TelegramLinkStatus>("/api/account/me/telegram", { method: "DELETE" }),
    onSuccess: (data) => {
      queryClient.setQueryData(TELEGRAM_LINK_KEY(accountId), data);
    },
  });
}

/** #2071 — claim a chat-bound link token for the signed-in account. On
 *  success the link status is read again (the screen flips to Connected). */
export function useClaimTelegram() {
  const { accountId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<TelegramClaimResult, Error, string>({
    mutationFn: (token) =>
      apiFetch<TelegramClaimResult>("/api/account/me/telegram/claim", {
        method: "POST",
        body: JSON.stringify({ token }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TELEGRAM_LINK_KEY(accountId) });
    },
  });
}

/** #2071 — whose Telegram chat a `/connect/telegram?t=` link would attach,
 *  read before Connect so a forwarded link cannot quietly route roster news
 *  to a stranger's chat. Never retried; a 410 means the link is spent. */
export function useTelegramClaimPreview(token: string) {
  const { accountId } = useAuth();
  return useQuery<TelegramClaimPreview, Error>({
    queryKey: ["me", "telegram", "claim-preview", accountId ?? "", token],
    enabled: Boolean(accountId && token),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: () =>
      apiFetch<TelegramClaimPreview>(
        `/api/account/me/telegram/claim-preview?t=${encodeURIComponent(token)}`,
      ),
  });
}

/** True for the claim's 410: the link was used, is stale, or never existed. */
export function isExpiredClaim(error: unknown): boolean {
  return error instanceof ApiError && error.status === 410;
}

/** The lead's People column. `undefined` data when the team has it off (404). */
export function useTeamTelegramStatus(teamId: string | undefined, enabled = true) {
  const { accountId } = useAuth();
  return useQuery<TeamTelegramStatusResponse, Error>({
    queryKey: ["team", teamId ?? "", "telegram-status", accountId ?? ""],
    enabled: enabled && Boolean(teamId && accountId),
    retry: false,
    staleTime: 30_000,
    queryFn: () =>
      apiFetch<TeamTelegramStatusResponse>(`/api/teams/${teamId}/members/telegram-status`),
  });
}
